export type PreviewMode = 'operator' | 'demo' | 'live';
export type PreviewRole = 'operator' | 'client';
export type ClientMode = 'demo' | 'live';
export type WorkspaceLifecycle = 'Demo' | 'Onboarding' | 'Live' | 'Paused';
export type WorkspaceHealth = 'Healthy' | 'Watch' | 'At risk';
export type BillingStatus = 'Demo' | 'Setup paid' | 'Active' | 'Past due' | 'Paused';

export interface ClientWorkspace {
  id: string;
  name: string;
  company?: string;
  email: string;
  phone: string;
  timezone: string;
  vertical: string;
  licensedStates: string[];
  lifecycle: WorkspaceLifecycle;
  health: WorkspaceHealth;
  activationDate: string | null;
  paidMonths: number;
  setupFeePaid: number;
  mrr: number;
  leads30d: number;
  bookings30d: number;
  transfers30d: number;
  contactRate: number;
  liveSessions: number;
  source: string;
  aiName: string;
  transferHours: string;
  crmDestination: string;
  billingStatus: BillingStatus;
  internalNotes: string;
  tags: string[];
  paused: boolean;
}

export interface ActivationState {
  completed: string[];
  activated: boolean;
}

const baseFee = 1_997;
const monthlyFee = 1_497;

export const INITIAL_WORKSPACES: ClientWorkspace[] = [
  {
    id: 'northstar', name: 'Dana Brooks', company: 'Northstar Coverage', email: 'dana.brooks@example.com', phone: '(212) 555-0142', timezone: 'America/New_York', vertical: 'Mortgage protection', licensedStates: ['NY', 'NJ', 'PA'], lifecycle: 'Demo', health: 'Healthy', activationDate: null, paidMonths: 0, setupFeePaid: 0, mrr: 0, leads30d: 136, bookings30d: 18, transfers30d: 11, contactRate: 68, liveSessions: 1, source: 'Paid social lead form', aiName: 'Ava', transferHours: 'Mon-Fri · 9:00 AM-6:00 PM ET', crmDestination: 'CRM · Northstar pipeline', billingStatus: 'Demo', internalNotes: 'Sales walkthrough account. Final CRM receipt remains before activation.', tags: ['Mortgage protection', 'NY market'], paused: false,
  },
  {
    id: 'keystone', name: 'Priya Nair', company: 'Keystone Insurance', email: 'priya.nair@example.com', phone: '(404) 555-0126', timezone: 'America/New_York', vertical: 'Mortgage protection', licensedStates: ['GA', 'SC', 'NC', 'FL'], lifecycle: 'Live', health: 'Healthy', activationDate: '2026-02-15', paidMonths: 6, setupFeePaid: baseFee, mrr: monthlyFee, leads30d: 482, bookings30d: 47, transfers30d: 28, contactRate: 63, liveSessions: 2, source: 'Paid social + web funnel', aiName: 'Maya', transferHours: 'Mon-Sat · 8:30 AM-7:00 PM ET', crmDestination: 'CRM · Inbound opportunities', billingStatus: 'Active', internalNotes: 'Strongest booking rate. Review evening transfer coverage next month.', tags: ['Scale', 'Southeast'], paused: false,
  },
  {
    id: 'shield', name: 'Marcus Holloway', company: 'Shield Life Group', email: 'marcus.holloway@example.com', phone: '(816) 555-0151', timezone: 'America/Chicago', vertical: 'Final expense', licensedStates: ['MO', 'KS', 'IA', 'NE'], lifecycle: 'Live', health: 'Watch', activationDate: '2026-01-10', paidMonths: 7, setupFeePaid: baseFee, mrr: monthlyFee, leads30d: 395, bookings30d: 31, transfers30d: 16, contactRate: 51, liveSessions: 1, source: 'Paid social lead form', aiName: 'Sam', transferHours: 'Mon-Fri · 9:00 AM-5:30 PM CT', crmDestination: 'CRM · Final expense queue', billingStatus: 'Active', internalNotes: 'Voice utilization at 90% of preferred monthly allocation.', tags: ['Final expense', 'Usage watch'], paused: false,
  },
  {
    id: 'heartland', name: 'Aaliyah Washington', company: 'Heartland Protection', email: 'aaliyah.washington@example.com', phone: '(615) 555-0168', timezone: 'America/Chicago', vertical: 'Final expense', licensedStates: ['TN', 'KY', 'AL'], lifecycle: 'Onboarding', health: 'Watch', activationDate: null, paidMonths: 0, setupFeePaid: baseFee, mrr: 0, leads30d: 0, bookings30d: 0, transfers30d: 0, contactRate: 0, liveSessions: 0, source: 'Awaiting source', aiName: 'Ivy', transferHours: 'Mon-Fri · 10:00 AM-6:00 PM CT', crmDestination: 'Pending destination test', billingStatus: 'Setup paid', internalNotes: 'Calendar authorization and final transfer greeting are pending.', tags: ['Onboarding', 'Final expense'], paused: false,
  },
  {
    id: 'delta', name: 'Monique Delacroix', company: 'Delta Shield Insurance', email: 'monique.delacroix@example.com', phone: '(504) 555-0184', timezone: 'America/Chicago', vertical: 'Mortgage protection', licensedStates: ['LA', 'MS', 'TX'], lifecycle: 'Live', health: 'Healthy', activationDate: '2026-03-01', paidMonths: 5, setupFeePaid: baseFee, mrr: monthlyFee, leads30d: 428, bookings30d: 39, transfers30d: 25, contactRate: 60, liveSessions: 1, source: 'Paid social lead form', aiName: 'Zoe', transferHours: 'Mon-Sat · 9:00 AM-7:00 PM CT', crmDestination: 'CRM · Licensed agent queue', billingStatus: 'Active', internalNotes: 'Bilingual routing trial planned for next review.', tags: ['Bilingual planned', 'Gulf states'], paused: false,
  },
  {
    id: 'ruiz', name: 'Sofia Ruiz', company: 'Ruiz Family Insurance', email: 'sofia.ruiz@example.com', phone: '(602) 555-0119', timezone: 'America/Phoenix', vertical: 'General life', licensedStates: ['AZ', 'NM', 'NV'], lifecycle: 'Paused', health: 'At risk', activationDate: '2026-02-20', paidMonths: 6, setupFeePaid: baseFee, mrr: monthlyFee, leads30d: 204, bookings30d: 12, transfers30d: 6, contactRate: 44, liveSessions: 0, source: 'Web funnel', aiName: 'Leo', transferHours: 'Mon-Fri · 9:00 AM-5:00 PM MT', crmDestination: 'CRM · Main pipeline', billingStatus: 'Past due', internalNotes: 'Outbound number quarantined after carrier reputation alert. Outreach paused.', tags: ['Paused', 'Number review'], paused: true,
  },
  {
    id: 'cameron', name: 'Cameron Bell', company: '', email: 'cameron.bell@example.com', phone: '(214) 555-0196', timezone: 'America/Chicago', vertical: 'Mortgage protection', licensedStates: ['TX'], lifecycle: 'Demo', health: 'Healthy', activationDate: null, paidMonths: 0, setupFeePaid: 0, mrr: 0, leads30d: 84, bookings30d: 7, transfers30d: 3, contactRate: 49, liveSessions: 0, source: 'Sample web funnel', aiName: 'Grace', transferHours: 'Mon-Fri · 11:00 AM-6:00 PM CT', crmDestination: 'Not connected', billingStatus: 'Demo', internalNotes: 'Individual-agent demo without a company name.', tags: ['Individual agent', 'Demo'], paused: false,
  },
];

export const DEFAULT_WORKSPACE_ID = 'northstar';
export const SETUP_FEE = baseFee;
export const MONTHLY_FEE = monthlyFee;

export function clientModeForWorkspace(workspace: ClientWorkspace): ClientMode {
  return workspace.lifecycle === 'Demo' || workspace.lifecycle === 'Onboarding' ? 'demo' : 'live';
}

export function workspaceMonthsActive(workspace: ClientWorkspace): number {
  return workspace.activationDate ? workspace.paidMonths : 0;
}

export function workspaceLtv(workspace: ClientWorkspace): number {
  return workspace.setupFeePaid + workspace.mrr * workspace.paidMonths;
}

export function workspaceDisplayName(workspace: ClientWorkspace): string {
  return workspace.company ? `${workspace.name} · ${workspace.company}` : workspace.name;
}
